import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Container, IconButton, MenuItem, TextField, Tooltip, Typography, CircularProgress } from "@mui/material";
import { Email, FileDownload, Folder, Print, WhatsApp } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import StandardPagination, { ITENS_POR_PAGINA_PADRAO } from "../components/Pagination/StandardPagination";
import { redirectToLogin } from "../services/httpClient";
import { birthdaysService, BirthdayRow } from "../services/birthdaysService";
import { useNavigation } from "../contexts/RouterContext";
import { filterFieldSx, searchFieldSx } from "../theme/filterStyles";
import { colors } from "../theme/designSystem";
import { useSensitiveDataAccess } from "../hooks/useSensitiveDataAccess";
import { SensitiveCell } from "../components/SensitiveField";
import { whatsAppLink } from "../utils/phone";

/**
 * Aniversariantes (RN-524, 05/09/2026): a lista completa que morava no Dashboard vira página
 * própria — período livre (padrão: mês corrente), todos os pacientes ativos da base, com as
 * ações de contato e exportação em Excel. Endpoint próprio (`/birthdays`), com nome completo —
 * o Dashboard mascara. O Dashboard fica só com a caixa "semana atual e próxima".
 */
const toIso = (d: Date): string => d.toISOString().slice(0, 10);

const monthStart = (): string => {
  const now = new Date();
  return toIso(new Date(now.getFullYear(), now.getMonth(), 1));
};

const monthEnd = (): string => {
  const now = new Date();
  return toIso(new Date(now.getFullYear(), now.getMonth() + 1, 0));
};

const Birthdays: React.FC = () => {
  const { goToDashboard, goToPatientRegister } = useNavigation();
  const initialFrom = monthStart();
  const initialTo = monthEnd();

  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);
  const [search, setSearch] = useState("");
  const [gender, setGender] = useState("");
  const [rows, setRows] = useState<BirthdayRow[]>([]);
  // RN-112q: a API já mascara telefone/e-mail; as flags só põem o cadeado na célula
  const { restricted: sensitiveRestricted } = useSensitiveDataAccess();
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITENS_POR_PAGINA_PADRAO);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await birthdaysService.list(from, to);
      setRows(data.items);
    } catch {
      setError("Não foi possível carregar os aniversariantes.");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setCurrentPage(1); }, [from, to, search, gender]);

  const normalize = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const genders = useMemo(
    () => Array.from(new Set(rows.map((r) => r.gender).filter((g): g is string => Boolean(g)))).sort(),
    [rows],
  );
  const filtered = useMemo(() => {
    const term = normalize(search.trim());
    return rows.filter((r) => {
      if (gender && r.gender !== gender) return false;
      if (term && !normalize(r.name).includes(term) && !normalize(r.responsibleName ?? "").includes(term)) return false;
      return true;
    });
  }, [rows, search, gender]);

  const hasFilterChanges = from !== initialFrom || to !== initialTo || search !== "" || gender !== "";
  const clearFilters = () => { setFrom(initialFrom); setTo(initialTo); setSearch(""); setGender(""); };

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paged = useMemo(
    () => filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [filtered, currentPage, itemsPerPage],
  );

  const handleRevalidateLogin = () => {
    localStorage.removeItem("clinic4us-user-session");
    localStorage.removeItem("clinic4us-remember-me");
    redirectToLogin();
  };

  const cellSx = (p: BirthdayRow) => ({ fontWeight: p.isToday ? 700 : 400 });

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const blob = await birthdaysService.exportXlsx(from, to);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `aniversariantes_${from}_${to}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Não foi possível exportar a planilha. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box className="c4u-print-page" sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        notificationCount={0}
        onRevalidateLogin={handleRevalidateLogin}
        onLogoClick={goToDashboard}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Aniversariantes</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Pacientes ativos que fazem aniversário no período, com contato do responsável.
              </Typography>
            </Box>
            <FaqButton />
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Nome"
                  placeholder="Paciente ou responsável"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  inputProps={{ maxLength: 100 }}
                  InputLabelProps={{ shrink: true }}
                  sx={searchFieldSx}
                />
                <TextField
                  select
                  size="small"
                  label="Gênero"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={filterFieldSx}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {genders.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </TextField>
                <TextField
                  size="small"
                  type="date"
                  label="De"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={filterFieldSx}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Até"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={filterFieldSx}
                />
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
                <Tooltip title="Exportar para Excel (período inteiro)">
                  <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={handleExport} disabled={loading || exporting || rows.length === 0} aria-label="Exportar para Excel">
                    <FileDownload fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Imprimir a lista">
                  <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={() => window.print()} disabled={loading || filtered.length === 0} aria-label="Imprimir">
                    <Print fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>

            {error && (
              <Box className="c4u-info-box c4u-info-box--warning" sx={{ mb: 2 }}>
                <Typography>{error}</Typography>
              </Box>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={28} sx={{ color: colors.primary }} />
              </Box>
            ) : (
              <>
                <ResultCount shown={filtered.length} total={rows.length} />

                <Box className="c4u-table">
                  <Box className="c4u-table__header">
                    <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 120px" }}>Nascimento</Box>
                    <Box className="admin-plans-header-cell c4u-col--sm" sx={{ flex: "0 0 70px" }}>Idade</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "2 1 240px", minWidth: 200 }}>Nome</Box>
                    <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "1 1 200px", minWidth: 160 }}>Responsável</Box>
                    <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "0 0 140px", minWidth: 130 }}>Telefone</Box>
                    <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "1 1 200px", minWidth: 160 }}>Email</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 130px", marginLeft: "auto", textAlign: "right" }}>Ações</Box>
                  </Box>

                  {paged.length === 0 && (
                    <Typography sx={{ color: colors.textSecondary, fontSize: "0.875rem", py: 3, textAlign: "center" }}>
                      Nenhum aniversariante no período.
                    </Typography>
                  )}

                  {paged.map((p) => (
                    <Box key={`${p.patientId}-${p.birth}`} className="c4u-table__row">
                      <Box className="admin-plans-cell c4u-col--md" data-label="Nascimento" sx={{ flex: "0 0 120px", ...cellSx(p) }}>
                        {p.birth}{p.isToday ? " 🎂" : ""}
                      </Box>
                      <Box className="admin-plans-cell c4u-col--sm" data-label="Idade" sx={{ flex: "0 0 70px", ...cellSx(p) }}>
                        {p.age ?? "-"}
                      </Box>
                      <Box className="admin-plans-cell" data-label="Nome" sx={{ flex: "2 1 240px", minWidth: 200, ...cellSx(p) }}>
                        {p.name}
                      </Box>
                      <Box className="admin-plans-cell c4u-col--lg" data-label="Responsável" sx={{ flex: "1 1 200px", minWidth: 160, color: p.responsibleName ? "inherit" : colors.textSecondary }}>
                        {p.responsibleName ?? "Próprio paciente"}
                      </Box>
                      <Box className="admin-plans-cell c4u-col--xl" data-label="Telefone" sx={{ flex: "0 0 140px", minWidth: 130, fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                        {sensitiveRestricted("phones") ? <SensitiveCell /> : p.phone ?? "—"}
                      </Box>
                      <Box className="admin-plans-cell c4u-col--xl" data-label="Email" sx={{ flex: "1 1 200px", minWidth: 160, fontSize: "0.8rem", wordBreak: "break-word" }}>
                        {sensitiveRestricted("emails") ? <SensitiveCell /> : p.email ?? "—"}
                      </Box>
                      <Box className="admin-plans-cell" data-label="Ações" sx={{ flex: "0 0 130px", marginLeft: "auto", display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                        {sensitiveRestricted("emails") && (
                          <Tooltip title="Restrito"><span>
                            <IconButton className="c4u-row-action" disabled size="small" sx={{ "&.Mui-disabled": { bgcolor: "#e9ecef", color: "#adb5bd" } }}><Email fontSize="small" /></IconButton>
                          </span></Tooltip>
                        )}
                        {p.email && (
                          <Tooltip title="Enviar email">
                            <IconButton className="c4u-row-action" component="a" href={`mailto:${p.email}?subject=Feliz Aniversário!`} target="_blank" rel="noopener noreferrer" size="small" sx={{ bgcolor: "#ff9800", color: "white", "&:hover": { bgcolor: "#f57c00" } }}>
                              <Email fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {sensitiveRestricted("phones") && (
                          <Tooltip title="Restrito"><span>
                            <IconButton className="c4u-row-action" disabled size="small" sx={{ "&.Mui-disabled": { bgcolor: "#e9ecef", color: "#adb5bd" } }}><WhatsApp fontSize="small" /></IconButton>
                          </span></Tooltip>
                        )}
                        {p.whatsAppPhone && (
                          <Tooltip title="Enviar mensagem no WhatsApp">
                            <IconButton className="c4u-row-action" component="a" href={whatsAppLink(p.whatsAppPhone, `Olá ${p.name.split(" ")[0]}! Feliz aniversário! 🎉`) ?? undefined} target="_blank" rel="noopener noreferrer" size="small" sx={{ bgcolor: "#25D366", color: "white", "&:hover": { bgcolor: "#1da851" } }}>
                              <WhatsApp fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Abrir cadastro do paciente">
                          <IconButton className="c4u-row-action" onClick={() => goToPatientRegister(p.patientId)} size="small" sx={{ bgcolor: "#03a9f4", color: "white", "&:hover": { bgcolor: "#0288d1" } }}>
                            <Folder fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {filtered.length > 0 && (
                  <StandardPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    itemsPerPage={itemsPerPage}
                    totalItems={filtered.length}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
                  />
                )}
              </>
            )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default Birthdays;
